import React, { useMemo, useState, useEffect } from "react";
import {
	XMarkIcon,
	ChevronDownIcon,
	ChevronUpIcon,
} from "@heroicons/react/24/outline";
import { useTicketStore } from "../../../store/ticketStore";
import { useSeatmapStore } from "../../../store/seatmapStore";
import { TICKET_TYPES } from "../../../utils/helpers";
import { parseSeatRange } from "../../../utils/seatRange";

const getLocalDateString = (date = new Date()) => {
	const year = date.getFullYear();
	const month = `${date.getMonth() + 1}`.padStart(2, "0");
	const day = `${date.getDate()}`.padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const getLocalTimeString = (date = new Date()) => {
	const hours = `${date.getHours()}`.padStart(2, "0");
	const minutes = `${date.getMinutes()}`.padStart(2, "0");
	return `${hours}:${minutes}`;
};

const EditTicketModal = ({ isOpen, onClose, ticket }) => {
	// 티켓 스토어에서 필요한 함수 가져오기
	const {
		updateTicket,
		getTicketDetail,
		deleteTicketItem,
		addTicketItem,
	} = useTicketStore();
	const {
		trainTypes,
		fetchTrainTypes,
		isLoading: isTrainTypeLoading,
		error: trainTypeError,
	} = useSeatmapStore();
	const [ticketDetails, setTicketDetails] = useState(null);

	// 초기 상태 값 정의
	const initialTicketState = {
		ticket_id: "",
		reservation_number: "",
		departure_date: getLocalDateString(),
		departure_time: getLocalTimeString(),
		arrival_time: getLocalTimeString(),
		train_code: "",
		train_type_code: "",
		departure_station: "",
		arrival_station: "",
		seat_info_list: [
			{
				train_car_number: "",
				seat_code: "",
			},
		],
		ticket_type: "일반승차권",
	};

	// 상태 관리
	const [ticketDataList, setTicketDataList] = useState([initialTicketState]);
	// 현재 선택된 티켓 인덱스
	const [selectedTicketIndex, setSelectedTicketIndex] = useState(0);
	// 현재 선택된 티켓 데이터 (편의를 위한 참조)
	const ticketData = ticketDataList[selectedTicketIndex];
	const [selectedImage, setSelectedImage] = useState(null);
	const [imagePreview, setImagePreview] = useState("");
	const [isSaving, setIsSaving] = useState(false);
	const [isTicketTypeDropdownOpen, setIsTicketTypeDropdownOpen] =
		useState(false);
	const trainTypeOptions = trainTypes;
	const trainTypeCodes = useMemo(
		() => new Set(trainTypes.map((type) => type.code)),
		[trainTypes]
	);

	// 드롭다운 외부 클릭 시 닫히는 기능
	useEffect(() => {
		if (!isOpen) {
			return;
		}

		if (trainTypes.length === 0) {
			fetchTrainTypes(true);
		}

		const handleClickOutside = (event) => {
			// 드롭다운이 열려있고, 클릭이 드롭다운 외부에서 발생한 경우
			if (isTicketTypeDropdownOpen) {
				// 클릭된 요소가 드롭다운 버튼이나 드롭다운 메뉴에 포함되지 않은 경우
				if (!event.target.closest(".ticket-type-dropdown")) {
					setIsTicketTypeDropdownOpen(false);
				}
			}
		};

		// 이벤트 리스너 추가
		document.addEventListener("mousedown", handleClickOutside);

		// 컴포넌트 언마운트 시 이벤트 리스너 제거
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [fetchTrainTypes, isOpen, isTicketTypeDropdownOpen, trainTypes.length]);

	// 티켓 정보가 변경될 때 상태 업데이트
	useEffect(() => {
		const fetchTicketDetail = async () => {
			if (ticket && isOpen) {
				try {
					// 티켓 상세 정보 API 호출
					const result = await getTicketDetail(ticket.ticket_id);

					if (result.success) {
						setTicketDetails(result.data);

						// 모든 티켓 데이터 처리
						const formattedTickets = result.data.map((item) => {
							// 출발역과 도착역 분리
							let departure_station = "";
							let arrival_station = "";

							if (item.departure_arrival_stations) {
								const stations =
									item.departure_arrival_stations.split("-");
								departure_station = stations[0] || "";
								arrival_station = stations[1] || "";
							} else if (
								item.departure_station &&
								item.arrival_station
							) {
								departure_station = item.departure_station;
								arrival_station = item.arrival_station;
							}

							// 티켓 데이터 포맷팅
							return {
								ticket_id: item.id || item.ticket_id,
								ticket_item_id: item.ticket_item_id,
								reservation_number:
									item.reservation_number || "",
								departure_date:
									item.departure_date || getLocalDateString(),
								departure_time:
									item.departure_time || getLocalTimeString(),
								arrival_time:
									item.arrival_time || getLocalTimeString(),
								train_code:
									item.train_code || item.train_codes || "",
								train_type_code: item.train_type_code || "",
								departure_station: departure_station,
								arrival_station: arrival_station,
								seat_info_list: item.seat_info_list?.map(
									(ticket) => {
										// 좌석 코드 파싱
										const seatParts =
											ticket.seat_code?.split(" ") || [
												"",
												"",
											];
										return {
											train_car_number:
												seatParts[0] || "",
											seat_code: seatParts[1] || "",
											user_name: ticket.user_name || "",
											user_phone: ticket.user_phone || "",
											user_reservation_cnt:
												ticket.user_reservation_cnt ||
												"",
											seat_info_id:
												ticket.seat_info_id || "",
											user_id: ticket.user_id || "",
										};
									}
								) || [
									{
										train_car_number: "",
										seat_code: "",
										user_name: "",
										user_phone: "",
										user_reservation_cnt: "",
										user_id: "",
									},
								],
								ticket_type: item.ticket_type || "일반승차권",
							};
						});

						// 티켓 데이터 저장
						setTicketDataList(formattedTickets);

						// 이미지 미리보기 설정 (모든 티켓의 이미지는 동일하다고 가정)
						if (
							result.data[0]?.image_url ||
							result.data[0]?.s3_url
						) {
							setImagePreview(
								result.data[0].image_url ||
									result.data[0].s3_url
							);
						}
					} else {
						console.error(
							"Failed to fetch ticket details:",
							result.error
						);
						alert("티켓 정보를 가져오는데 실패했습니다.");
					}
				} catch (error) {
					console.error("Error fetching ticket details:", error);
					alert("티켓 정보를 가져오는 중 오류가 발생했습니다.");
				}
			}
		};

		fetchTicketDetail();
	}, [ticket, isOpen, getTicketDetail]);

	// 모달이 닫힐 때 상태 초기화
	useEffect(() => {
		if (!isOpen) {
			setTicketDataList([initialTicketState]);
			setSelectedTicketIndex(0);
			setSelectedImage(null);
			setImagePreview("");
			setIsSaving(false);
			setIsTicketTypeDropdownOpen(false);
		}
	}, [isOpen]);

	// 티켓 정보 변경 핸들러
	const handleTicketChange = (field, value) => {
		const updatedTicketList = [...ticketDataList];

		// 티켓 타입은 모든 티켓에 일괄 적용
		if (field === "ticket_type") {
			updatedTicketList.forEach((ticket, index) => {
				updatedTicketList[index] = {
					...ticket,
					ticket_type: value,
				};
			});
		} else {
			// 다른 필드는 현재 선택된 티켓만 변경
			updatedTicketList[selectedTicketIndex] = {
				...updatedTicketList[selectedTicketIndex],
				[field]: value,
			};
		}

		setTicketDataList(updatedTicketList);
	};

	// 좌석 정보 변경 핸들러
	const handleSeatChange = (seatIndex, field, value) => {
		const updatedTicketList = [...ticketDataList];
		const updatedSeatInfoList = [
			...updatedTicketList[selectedTicketIndex].seat_info_list,
		];

		// 호차 입력 처리 (숫자만 입력 가능)
		if (field === "train_car_number" && value) {
			// 숫자만 추출
			value = value.replace(/[^0-9]/g, "");
		}

		updatedSeatInfoList[seatIndex][field] = value;

		updatedTicketList[selectedTicketIndex] = {
			...updatedTicketList[selectedTicketIndex],
			seat_info_list: updatedSeatInfoList,
		};

		setTicketDataList(updatedTicketList);
	};

	const applySeatRange = (seatIndex, seatCode) => {
		const result = parseSeatRange(seatCode);
		if (!result.isRange || result.error || result.seats.length === 0) {
			return false;
		}

		const updatedTicketList = [...ticketDataList];
		const seatInfoList = [
			...updatedTicketList[selectedTicketIndex].seat_info_list,
		];
		const currentSeat = seatInfoList[seatIndex] ?? {};
		const emptySeatTemplate = {
			train_car_number: currentSeat.train_car_number ?? "",
			user_name: "",
			user_phone: "",
			user_reservation_cnt: 0,
			user_id: "",
		};

		seatInfoList[seatIndex] = {
			...currentSeat,
			seat_code: result.seats[0],
		};

		const additionalSeats = result.seats.slice(1).map((code) => ({
			...emptySeatTemplate,
			seat_code: code,
		}));

		seatInfoList.splice(seatIndex + 1, 0, ...additionalSeats);
		updatedTicketList[selectedTicketIndex] = {
			...updatedTicketList[selectedTicketIndex],
			seat_info_list: seatInfoList,
		};
		setTicketDataList(updatedTicketList);
		return true;
	};

	// 호차 입력 후 처리
	const handleTrainCarBlur = (seatIndex) => {
		const updatedTicketList = [...ticketDataList];
		const updatedSeatInfoList = [
			...updatedTicketList[selectedTicketIndex].seat_info_list,
		];
		let value = updatedSeatInfoList[seatIndex].train_car_number;

		if (value && !value.includes("호차")) {
			updatedSeatInfoList[seatIndex].train_car_number = `${value}호차`;

			updatedTicketList[selectedTicketIndex] = {
				...updatedTicketList[selectedTicketIndex],
				seat_info_list: updatedSeatInfoList,
			};

			setTicketDataList(updatedTicketList);
		}
	};

	// 좌석 정보 추가 핸들러
	const handleAddSeat = () => {
		const newSeat = {
			train_car_number: "",
			seat_code: "",
			user_name: "",
			user_phone: "",
			user_reservation_cnt: 0,
			user_id: "",
		};

		const updatedTicketList = [...ticketDataList];
		updatedTicketList[selectedTicketIndex] = {
			...updatedTicketList[selectedTicketIndex],
			seat_info_list: [
				...updatedTicketList[selectedTicketIndex].seat_info_list,
				newSeat,
			],
		};

		setTicketDataList(updatedTicketList);
	};

	// 좌석 정보 삭제 핸들러
	const handleRemoveSeat = (index) => {
		// 적어도 하나의 좌석은 남겨두어야 함
		if (ticketDataList[selectedTicketIndex].seat_info_list.length <= 1) {
			alert("적어도 하나의 좌석 정보가 필요합니다.");
			return;
		}

		const updatedTicketList = [...ticketDataList];
		const updatedSeatInfoList = [
			...updatedTicketList[selectedTicketIndex].seat_info_list,
		];
		updatedSeatInfoList.splice(index, 1);

		updatedTicketList[selectedTicketIndex] = {
			...updatedTicketList[selectedTicketIndex],
			seat_info_list: updatedSeatInfoList,
		};

		setTicketDataList(updatedTicketList);
	};

	// 이미지 업로드 핸들러
	const handleImageChange = (e) => {
		const file = e.target.files[0];
		if (file) {
			setSelectedImage(file);
			const reader = new FileReader();
			reader.onloadend = () => {
				setImagePreview(reader.result);
			};
			reader.readAsDataURL(file);
		}
	};

	// 티켓 아이템 삭제 핸들러
	const handleDeleteTicketItem = async (index) => {
		// 마지막 하나만 남은 경우 삭제 불가
		if (ticketDataList.length <= 1) {
			alert("적어도 하나의 열차는 유지되어야 합니다.");
			return;
		}

		try {
			const ticketItemId = ticketDataList[index].ticket_item_id;
			if (!ticketItemId) {
				// 새로 추가한 열차는 아직 저장되지 않았으므로 그냥 UI에서만 삭제
				const updatedList = [...ticketDataList];
				updatedList.splice(index, 1);
				setTicketDataList(updatedList);

				// 현재 선택된 티켓 인덱스 조정
				if (selectedTicketIndex >= updatedList.length) {
					setSelectedTicketIndex(updatedList.length - 1);
				} else if (selectedTicketIndex === index) {
					setSelectedTicketIndex(0);
				}
				return;
			}

			// 확인 메시지
			if (!confirm(`열차 #${index + 1}을(를) 삭제하시겠습니까?`)) {
				return;
			}

			// API 호출
			const result = await deleteTicketItem(ticketItemId);

			if (result.success) {
				// UI에서 삭제
				const updatedList = [...ticketDataList];
				updatedList.splice(index, 1);
				setTicketDataList(updatedList);

				// 현재 선택된 티켓 인덱스 조정
				if (selectedTicketIndex >= updatedList.length) {
					setSelectedTicketIndex(updatedList.length - 1);
				} else if (selectedTicketIndex === index) {
					setSelectedTicketIndex(0);
				}

				alert("열차가 삭제되었습니다.");
			} else {
				alert(result.error || "열차 삭제 중 오류가 발생했습니다.");
			}
		} catch (error) {
			console.error("열차 삭제 오류:", error);
			alert("티켓 삭제 중 오류가 발생했습니다.");
		}
	};

	// 티켓 추가 핸들러
	const handleAddTicket = () => {
		// 초기 상태 값을 사용하여 새 티켓 추가
		const newTicket = {
			...initialTicketState,
			ticket_id: ticket.ticket_id, // 기본 티켓 ID 유지
			departure_date: getLocalDateString(),
			departure_time: getLocalTimeString(),
			arrival_time: getLocalTimeString(),
			ticket_type:
				Object.keys(TICKET_TYPES).find(
					(key) => TICKET_TYPES[key] === ticketDataList[0].ticket_type
				) || ticketDataList[0].ticket_type,
		};

		// 현재 티켓 목록에 추가
		const updatedList = [...ticketDataList, newTicket];
		setTicketDataList(updatedList);

		// 새로 추가된 티켓을 선택
		setSelectedTicketIndex(updatedList.length - 1);
	};

	// 저장 핸들러
	const handleSave = async () => {
		try {
			// 현재 선택된 티켓 기본 검증
			const currentTicket = ticketDataList[selectedTicketIndex];
			if (!currentTicket.reservation_number) {
				alert("예약번호를 입력해주세요.");
				return;
			}

			if (
				!currentTicket.departure_station ||
				!currentTicket.arrival_station
			) {
				alert("출발역과 도착역을 입력해주세요.");
				return;
			}

			const invalidTrainTypes = ticketDataList
				.map((item, index) => {
					const code = item.train_type_code?.trim();
					if (!code) {
						return null;
					}
					if (trainTypeCodes.size === 0) {
						return { index, code };
					}
					return trainTypeCodes.has(code) ? null : { index, code };
				})
				.filter(Boolean);

			if (invalidTrainTypes.length > 0) {
				const invalidTargets = invalidTrainTypes
					.map((item) => `#${item.index + 1} (${item.code})`)
					.join(", ");
				alert(
					`등록된 차종 목록에 없는 값입니다: ${invalidTargets}\n차종 목록에서 선택해 주세요.`
				);
				return;
			}

			if (trainTypeCodes.size === 0 && trainTypeError) {
				alert(
					"차종 목록을 불러오지 못했습니다. 잠시 후 다시 시도해주세요."
				);
				return;
			}

			setIsSaving(true);
			let allSuccess = true;
			let errorMessage = "";

			// 모든 티켓 처리 (기존 티켓 업데이트 및 새 티켓 추가)
			for (let i = 0; i < ticketDataList.length; i++) {
				const ticketData = ticketDataList[i];
				const ticketDetail =
					ticketDetails && i < ticketDetails.length
						? ticketDetails[i]
						: null;
				const isNewTicket =
					!ticketDetail || !ticketDetail.ticket_item_id;

				// 좌석 정보 포맷팅 함수 - 백엔드 SeatInfoWithUser 스키마에 맞춤
				const formatSeatInfoList = (seatList, ticketItemId = 0) => {
					return seatList.map((seat) => {
						// 백엔드 SeatInfoWithUser 스키마:
						// - id: int (required)
						// - ticket_item_id: int (required)
						// - train_car_number: int (required)
						// - seat_code: str (required)
						// - user_id: Optional[str]
						// - user_name: Optional[str]
						// - user_phone: Optional[str]
						// - user_reservation_cnt: int = 0
						const seatInfo = {
							id: seat.seat_info_id || 0,
							ticket_item_id: ticketItemId,
							train_car_number: parseInt(seat.train_car_number) || 0,
							seat_code: seat.seat_code || "",
							user_id: seat.user_id || null,
							user_name: seat.user_name || null,
							user_phone: seat.user_phone || null,
							user_reservation_cnt: parseInt(seat.user_reservation_cnt) || 0,
						};

						return seatInfo;
					});
				};

				// 이미지는 첫 번째 티켓에만 업로드 (모든 티켓이 동일한 이미지 사용)
				const imageToUpload = i === 0 ? selectedImage : null;

				let result;

				if (isNewTicket) {
					// 새로운 티켓 추가
					const newTicketData = {
						ticket_id: ticketData.ticket_id,
						train_code: ticketData.train_code,
						train_type_code: ticketData.train_type_code || null,
						reservation_number: ticketData.reservation_number,
						departure_station: ticketData.departure_station,
						arrival_station: ticketData.arrival_station,
						departure_date: ticketData.departure_date,
						departure_time: ticketData.departure_time,
						arrival_time: ticketData.arrival_time,
						ticket_type:
							Object.keys(TICKET_TYPES).find(
								(key) =>
									TICKET_TYPES[key] === ticketData.ticket_type
							) || ticketData.ticket_type,
						seat_info_list: ticketData.seat_info_list.map(
							(seat) => ({
								seat_code: `${seat.train_car_number} ${seat.seat_code}`,
								user_name: seat.user_name || "-",
								user_phone: seat.user_phone || "-",
								user_reservation_cnt:
									parseInt(seat.user_reservation_cnt) || 0,
							})
						),
					};

					result = await addTicketItem(newTicketData);
				} else {
					// 기존 티켓 업데이트
					const formattedData = {
						old_image_url: ticketDetail.image_url,
						ticket_type:
							Object.keys(TICKET_TYPES).find(
								(key) =>
									TICKET_TYPES[key] === ticketData.ticket_type
							) || ticketData.ticket_type,
						ticket_id: ticketData.ticket_id,
						ticket_item_id: ticketDetail.ticket_item_id,
						train_code: ticketData.train_code,
						train_type_code: ticketData.train_type_code || null,
						departure_station: ticketData.departure_station,
						arrival_station: ticketData.arrival_station,
						departure_date: ticketData.departure_date,
						departure_time: ticketData.departure_time,
						arrival_time: ticketData.arrival_time,
						reservation_number: ticketData.reservation_number,
						seat_info_list: formatSeatInfoList(
							ticketData.seat_info_list,
							ticketDetail.ticket_item_id
						),
					};

					result = await updateTicket(formattedData, imageToUpload);
				}

				if (!result.success) {
					allSuccess = false;
					errorMessage =
						result.error ||
						(isNewTicket
							? "새 티켓 추가에 실패했습니다."
							: "승차권 업데이트에 실패했습니다.");
					break;
				}
			}

			setIsSaving(false);

			if (allSuccess) {
				alert("승차권 정보가 업데이트되었습니다.");
				onClose(true);
			} else {
				alert(errorMessage || "승차권 업데이트에 실패했습니다.");
			}
		} catch (error) {
			console.error("티켓 업데이트 오류:", error);
			setIsSaving(false);
			alert("알 수 없는 오류가 발생했습니다.");
		}
	};

	if (!isOpen) return null;

	const ticketDataSection = (
		<div>
			<div className="grid grid-cols-2 gap-4">
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						예약번호
					</label>
					<input
						type="text"
						value={ticketData.reservation_number}
						onChange={(e) =>
							handleTicketChange(
								"reservation_number",
								e.target.value
							)
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
					/>
				</div>

				<div className="relative">
					<label className="block text-sm font-medium text-gray-700 mb-1">
						티켓 종류
					</label>

					{/* 현재 선택된 티켓 타입 및 드롭다운 버튼 */}
					<div
						className="ticket-type-dropdown flex items-center justify-between w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white cursor-pointer hover:bg-gray-50"
						onClick={() =>
							setIsTicketTypeDropdownOpen(
								!isTicketTypeDropdownOpen
							)
						}
					>
						<span className="block truncate">
							{ticketData.ticket_type === "normal"
								? "일반승차권"
								: ticketData.ticket_type === "voucher"
								? "바우처"
								: ticketData.ticket_type === "homeTicket"
								? "홈티켓"
								: ticketData.ticket_type === "srt"
								? "SRT"
								: ticketData.ticket_type === "special"
								? "특단"
								: "일반승차권"}
						</span>
						{isTicketTypeDropdownOpen ? (
							<ChevronUpIcon className="h-5 w-5 text-gray-400" />
						) : (
							<ChevronDownIcon className="h-5 w-5 text-gray-400" />
						)}
					</div>

					{/* 드롭다운 메뉴 */}
					{isTicketTypeDropdownOpen && (
						<div className="ticket-type-dropdown absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
							{[
								{
									label: "일반승차권",
									value: "normal",
								},
								{
									label: "바우처",
									value: "voucher",
								},
								{
									label: "홈티켓",
									value: "homeTicket",
								},
								{
									label: "SRT",
									value: "srt",
								},
								{
									label: "특단",
									value: "special",
								},
							].map((option) => (
								<div
									key={option.value}
									className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-50 ${
										ticketData.ticket_type === option.value
											? "bg-blue-100 text-blue-900"
											: "text-gray-900"
									}`}
									onClick={() => {
										handleTicketChange(
											"ticket_type",
											option.value
										);
										setIsTicketTypeDropdownOpen(false);
									}}
								>
									{option.label}
								</div>
							))}
						</div>
					)}

					<p className="mt-1 text-xs text-gray-500">
						모든 티켓에 일괄 적용됩니다.
					</p>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						출발일
					</label>
					<input
						type="date"
						value={ticketData.departure_date}
						onChange={(e) =>
							handleTicketChange("departure_date", e.target.value)
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						열차번호
					</label>
					<input
						type="text"
						value={ticketData.train_code}
						onChange={(e) =>
							handleTicketChange("train_code", e.target.value)
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						차종
					</label>
					<select
						value={ticketData.train_type_code || ""}
						onChange={(e) =>
							handleTicketChange("train_type_code", e.target.value)
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
						disabled={isTrainTypeLoading && trainTypeOptions.length === 0}
					>
						<option value="">선택 안 함</option>
						{ticketData.train_type_code &&
							!trainTypeCodes.has(ticketData.train_type_code) && (
								<option value={ticketData.train_type_code}>
									{ticketData.train_type_code} (등록되지 않음)
								</option>
							)}
						{trainTypeOptions.map((option) => (
							<option key={option.code} value={option.code}>
								{option.name}
								{option.is_active === false ? " (비활성)" : ""} (
								{option.code})
							</option>
						))}
					</select>
					{ticketData.train_type_code &&
						!trainTypeCodes.has(ticketData.train_type_code) && (
							<p className="mt-1 text-xs text-red-500">
								등록되지 않은 차종입니다. 목록에서 선택해야 저장할 수
								있습니다.
							</p>
						)}
					{trainTypeError && trainTypeOptions.length === 0 && (
						<p className="mt-1 text-xs text-red-500">
							차종 목록을 불러오지 못했습니다.
						</p>
					)}
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						출발역
					</label>
					<input
						type="text"
						value={ticketData.departure_station}
						onChange={(e) =>
							handleTicketChange(
								"departure_station",
								e.target.value
							)
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						도착역
					</label>
					<input
						type="text"
						value={ticketData.arrival_station}
						onChange={(e) =>
							handleTicketChange(
								"arrival_station",
								e.target.value
							)
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						출발시간
					</label>
					<input
						type="time"
						value={ticketData.departure_time || ""}
						onChange={(e) =>
							handleTicketChange("departure_time", e.target.value)
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-gray-700 mb-1">
						도착시간
					</label>
					<input
						type="time"
						value={ticketData.arrival_time || ""}
						onChange={(e) =>
							handleTicketChange("arrival_time", e.target.value)
						}
						className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
					/>
				</div>
			</div>

			{/* 좌석 정보 */}
			<div className="mt-4">
				<div className="flex justify-between items-center mb-2">
					<label className="block text-sm font-medium text-gray-700">
						좌석 정보
					</label>
					<button
						type="button"
						onClick={handleAddSeat}
						className="inline-flex items-center px-3 py-1 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
					>
						좌석 추가
					</button>
				</div>
				<div className="overflow-x-auto">
					<table className="min-w-full table-fixed divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th
									scope="col"
									className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider"
									style={{ width: "10%" }}
								>
									호차
								</th>
								<th
									scope="col"
									className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider"
									style={{ width: "10%" }}
								>
									좌석
								</th>
								<th
									scope="col"
									className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider"
									style={{ width: "20%" }}
								>
									사용자 정보
								</th>
								<th
									scope="col"
									className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider"
									style={{ width: "5%" }}
								>
									삭제
								</th>
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{ticketData.seat_info_list.map((seat, index) => {
								const rangeResult = parseSeatRange(seat.seat_code);

								return (
									<tr
										key={index}
										id={seat.seat_info_id || `seat-${index}`}
										className="inputClass"
									>
										<td className="px-3 py-2 align-top">
											<input
												type="text"
												placeholder="호차"
												value={seat.train_car_number}
												onChange={(e) =>
													handleSeatChange(
														index,
														"train_car_number",
														e.target.value
													)
												}
												onBlur={() =>
													handleTrainCarBlur(index)
												}
												className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
											/>
										</td>
										<td className="px-3 py-2 whitespace-nowrap">
											<div className="flex flex-col">
												<input
													type="text"
													placeholder="좌석번호"
													value={seat.seat_code}
													onChange={(e) =>
														handleSeatChange(
															index,
															"seat_code",
															e.target.value
														)
													}
													onKeyDown={(e) => {
														if (e.key !== "Enter") {
															return;
														}

														const result =
															parseSeatRange(
																e.target
																	.value
															);
														if (
															!result.isRange ||
															result.error
														) {
															return;
														}

														e.preventDefault();
														applySeatRange(
															index,
															e.target.value
														);
													}}
													className="w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
												/>
												<p
													className={`mt-1 min-h-[14px] text-xs leading-tight truncate ${
														rangeResult.isRange
															? rangeResult.error
																? "text-red-500"
																: "text-gray-400"
															: "invisible"
													}`}
												>
													{rangeResult.isRange
														? rangeResult.error
															? rangeResult.error
															: `Enter로 범위 좌석 생성 (${rangeResult.count}석)`
														: "\u00A0"}
												</p>
											</div>
										</td>
										<td className="px-3 py-2">
											<div className="text-sm text-gray-900">
												{seat.user_name && seat.user_phone
													? `${seat.user_name}(${
															seat.user_phone
													  }) / ${
															seat.user_reservation_cnt ||
															0
													  }명`
													: "-"}
											</div>
										</td>
										<td className="px-3 py-2 whitespace-nowrap text-right text-sm font-medium">
											<button
												type="button"
												onClick={() =>
													handleRemoveSeat(index)
												}
												className="text-red-600 hover:text-red-900"
											>
												삭제
											</button>
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</div>
		</div>
	);

	const imageUploadSection = (
		<div>
			<div className="mb-4">
				<label className="block text-sm font-medium text-gray-700 mb-2">
					승차권 이미지
				</label>
				<div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
					{imagePreview ? (
						<div className="w-full">
							<img
								src={imagePreview}
								alt="티켓 이미지 미리보기"
								className="max-h-[70vh] mx-auto"
							/>
							<div className="mt-2 flex justify-center">
								<label className="cursor-pointer bg-blue-600 text-white py-1 px-3 rounded-md text-sm">
									이미지 변경
									<input
										type="file"
										className="hidden"
										accept="image/*"
										onChange={handleImageChange}
									/>
								</label>
							</div>
						</div>
					) : (
						<div className="space-y-1 text-center">
							<div className="flex text-sm text-gray-600">
								<label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none">
									<span>이미지 업로드</span>
									<input
										type="file"
										className="sr-only"
										accept="image/*"
										onChange={handleImageChange}
									/>
								</label>
							</div>
							<p className="text-xs text-gray-500">
								PNG, JPG, GIF 최대 10MB
							</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			{/* 배경 오버레이 */}
			<div
				className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
				onClick={(e) => onClose(false)}
			></div>

			{/* 모달 컨테이너 */}
			<div className="flex min-h-screen items-center justify-center p-4">
				<div className="relative bg-white rounded-lg shadow-xl w-full max-w-[1400px]">
					{/* 모달 헤더 */}
					<div className="flex items-center justify-between p-4 border-b">
						<h3 className="text-xl font-semibold text-gray-900">
							승차권 상세 정보 수정
						</h3>
						<button
							onClick={(e) => onClose(false)}
							className="text-gray-400 hover:text-gray-500 focus:outline-none"
						>
							<XMarkIcon className="h-6 w-6" />
						</button>
					</div>

					{/* 모달 내용 */}
					<div className="p-6">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							{/* 이미지 표시 영역 - 왼쪽에 고정 */}
							{imageUploadSection}

							{/* 티켓 정보 입력 영역 - 오른쪽 */}
							<div>
								{/* 티켓 선택 탭 - 티켓 정보 입력 영역 위에 배치 */}
								<div className="my-7">
									<div className="flex justify-between items-center mb-4">
										<h3 className="text-lg font-medium text-gray-700">
											열차 목록
										</h3>
										<button
											type="button"
											className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ease-in-out transform hover:scale-105"
											onClick={handleAddTicket}
										>
											<svg
												xmlns="http://www.w3.org/2000/svg"
												className="h-5 w-5 mr-1"
												viewBox="0 0 20 20"
												fill="currentColor"
											>
												<path
													fillRule="evenodd"
													d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
													clipRule="evenodd"
												/>
											</svg>
											열차 추가
										</button>
									</div>
									<div
										className="flex flex-wrap gap-3"
										aria-label="티켓 탭"
									>
										{ticketDataList.map((ticket, index) => (
											<div
												key={index}
												className="relative group"
											>
												<button
													className={`
													py-2 px-6 rounded-lg font-medium text-base transition-all duration-200
													${
														selectedTicketIndex ===
														index
															? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg"
															: "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 shadow-sm"
													}
												`}
													onClick={() =>
														setSelectedTicketIndex(
															index
														)
													}
												>
													#{index + 1}
												</button>
												<button
													className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 shadow-md opacity-0 group-hover:opacity-100 transition-opacity duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
													onClick={(e) => {
														e.preventDefault();
														e.stopPropagation();
														handleDeleteTicketItem(
															index
														);
													}}
													title="열차 삭제"
												>
													<XMarkIcon className="h-4 w-4" />
												</button>
											</div>
										))}
									</div>
								</div>

								{/* 티켓 정보 입력 섹션 */}
								{ticketDataSection}
							</div>
						</div>
					</div>

					{/* 모달 푸터 */}
					<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b-lg">
						<button
							type="button"
							className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
							onClick={handleSave}
							disabled={isSaving}
						>
							{isSaving ? "저장 중..." : "저장"}
						</button>
						<button
							type="button"
							className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
							onClick={onClose}
						>
							취소
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default EditTicketModal;
